import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryWorkLog } from "../../../query/workLog/queries/useQueryWorkLog";
import { useQueryWorkLogs } from "../../../query/workLog/queries/useQueryWorkLogs";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { useSetWSStore } from "../../../store";
import { BulkReviewProps } from "../../BulkReview/types";
import { getPayeeNames } from "../../Payees/selectors/getPayeeNames";
import { routeHelpersWorkLogPayerDetails } from "../paths";
import { createListRequest } from "../components/TableWorkLogsPayerView/filters/request";
import { WorkLogPayerTabFilterStatus } from "../components/TableWorkLogsPayerView/filters/types";
import { usePayeesMap } from "./usePayeesMap";

export function useBulkReviewWorkLogs(workLogId?: string) {
  const history = useHistory();
  const location = useLocation();
  const setStore = useSetWSStore();
  const userId = useUserId();

  // Activate only when ?bulkReview=true is in the URL
  const isActive =
    new URLSearchParams(location.search).get("bulkReview") === "true";

  const request = useMemo(
    () =>
      createListRequest(userId, {
        status: WorkLogPayerTabFilterStatus.ToApprove
      }),
    [userId]
  );

  const queryWorkLogs = useQueryWorkLogs(request, {
    enabled: isActive,
    refetchOnMount: true
  });

  const queryWorkLogsSummary = useQueryWorkLogsSummary(request, {
    enabled: isActive
  });

  const workLogs = queryWorkLogs.data || [];

  const payeesMap = usePayeesMap(isActive ? workLogs : undefined);

  const bulkReviewProps = useMemo((): BulkReviewProps<IWorkLog> => {
    return {
      activeId: workLogId,
      listSize: queryWorkLogsSummary.data?.listSize,
      rows: workLogs.map(wl => {
        const payee = payeesMap.get(wl.payeeId);
        const payeeName = payee
          ? getPayeeNames(payee).getResolvedName()
          : undefined;
        return {
          id: wl.workLogId,
          text: payeeName || `Work log #${wl.workLogNumber}`,
          secondaryText: `Work log #${wl.workLogNumber}`,
          updatedAt: wl.updatedAt,
          data: wl
        };
      }),
      onRowClick: row => {
        const searchParams = Object.fromEntries(
          new URLSearchParams(location.search)
        ) as { bulkReview: string };
        history.push(
          routeHelpersWorkLogPayerDetails.createLocationObject(
            { workLogId: row.id },
            { search: searchParams }
          )
        );
      },
      fetchNextPage: queryWorkLogs.fetchNextPage,
      hasNextPage:
        !queryWorkLogs.isFetchingNextPage && queryWorkLogs.hasNextPage,
      onClose: () => {
        const params = new URLSearchParams(location.search);
        params.delete("bulkReview");
        const newSearch = params.toString();
        history.replace({ search: newSearch ? `?${newSearch}` : "" });
      }
    };
  }, [
    workLogId,
    workLogs,
    queryWorkLogsSummary.data?.listSize,
    queryWorkLogs.fetchNextPage,
    queryWorkLogs.hasNextPage,
    queryWorkLogs.isFetchingNextPage,
    history,
    location.search,
    payeesMap
  ]);

  // Show sidebar when active and has multiple items
  useEffect(() => {
    setStore({ bulkReviewVisible: isActive && workLogs.length > 1 });
    return () => {
      setStore({ bulkReviewVisible: false });
    };
  }, [isActive, workLogs.length, setStore]);

  // Update bulk review data in store
  useEffect(() => {
    setStore({ bulkReview: bulkReviewProps });
  }, [bulkReviewProps, setStore]);

  // Prefetch adjacent work logs for faster navigation
  const activeIndex = workLogs.findIndex(wl => wl.workLogId === workLogId);
  const prevWorkLog = activeIndex > 0 ? workLogs[activeIndex - 1] : undefined;
  const nextWorkLog =
    activeIndex < workLogs.length - 1 ? workLogs[activeIndex + 1] : undefined;

  useQueryWorkLog(prevWorkLog?.workLogId!, {
    enabled: !!prevWorkLog,
    staleTime: Infinity
  });
  useQueryWorkLog(nextWorkLog?.workLogId!, {
    enabled: !!nextWorkLog,
    staleTime: Infinity
  });

  return { queryWorkLogs };
}
